import { ENDPOINTS } from '../../api/endpoints'
import req from '../../api/request'

/**
 * A row from GET /search/admin/officials.
 *
 * `official_type` is the role stored on the PERSON — their usual job — not the
 * role they hold on any particular game. It seeds a new assignment's role and
 * nothing more.
 *
 * `full_name` is always null (there is no such accessor on the model); the real
 * composed name is `name_full`. Both are declared because the endpoint sends
 * both and the mobile client already reads the broken one.
 */
export interface AdminOfficialSearchResult {
  id: string
  name_first?: string | null
  name_middle?: string | null
  name_last?: string | null
  name_full?: string | null
  full_name?: string | null
  official_type?: number | null
  /**
   * Uid of the app account this record belongs to, or null when the invite was
   * never accepted.
   *
   * The only field that can tell "the same human, recorded twice" from "two
   * people who share a name" — names and emails cannot: some duplicated pairs
   * share an email, and some genuinely distinct accounts share a name.
   */
  user_id?: string | null
  /**
   * Opaque, stable key for the same PERSON across duplicate records — a hash of
   * their email, computed server-side so the address itself never travels.
   * Null when the record has no email.
   */
  identity_hash?: string | null
  image?: unknown
}

export interface SearchAdminOfficialsArgs {
  /** Organization uid. */
  organizationId: string
  /** Competition (root schedule) uid. */
  scheduleId: string
  query?: string
  signal?: AbortSignal
}

/**
 * The officials assignable within one organization + competition.
 *
 * BOTH scope params are required, and that is not a convenience: without them
 * the endpoint falls through to an unscoped branch that returns every official
 * in the database — thousands of unrelated people, unpaginated. The caller must
 * not fire this until it has both.
 *
 * Unpaginated by design on the server (`->get()`); a competition's pool is a few
 * dozen people, so the whole list is fetched once and filtered in the browser
 * rather than round-tripping on every keystroke.
 */
export const searchAdminOfficials = async ({
  organizationId,
  scheduleId,
  query,
  signal,
}: SearchAdminOfficialsArgs): Promise<AdminOfficialSearchResult[]> => {
  if (!organizationId || !scheduleId) {
    throw new Error(
      'searchAdminOfficials requires both an organization and a schedule; without them the endpoint returns every official on the platform.'
    )
  }

  const response = await req(ENDPOINTS.SEARCH.ADMIN_OFFICIALS(), {
    method: 'GET',
    query: {
      organization: organizationId,
      schedule: scheduleId,
      //  undefined → the query builder drops the param.
      query: query === '' ? undefined : query,
    },
    signal,
  })

  return (response?.data as AdminOfficialSearchResult[]) ?? []
}

/** `name_full`, falling back to the parts, falling back to nothing. */
export const officialDisplayName = (
  official: Pick<
    AdminOfficialSearchResult,
    'name_full' | 'name_first' | 'name_middle' | 'name_last'
  >
): string => {
  const composed = official.name_full?.trim()
  if (composed !== undefined && composed !== '') {
    return composed
  }
  return [official.name_first, official.name_middle, official.name_last]
    .map((part) => part?.trim() ?? '')
    .filter((part) => part !== '')
    .join(' ')
}
